import React, { useCallback, useMemo } from "react";
import { Grid, Heading, Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/react";
import { Formik } from "formik";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppListHeader from "@/features/ui/app-list-header";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useGetDenseOrderConfirmationInvoice, {
  GetDenseOrderConfirmationInvoiceParameter,
  GetDenseOrderConfirmationInvoiceRequest,
} from "@/features/invoices/order-confirmation-invoices/hooks/use-get-dense-order-confirmation-invoice";
import useOrderConfirmationInvoiceFormValidationSchema from "@/features/invoices/order-confirmation-invoices/hooks/use-order-confirmation-invoice-form-validation-schema";
import { useOrderConfirmationInvoiceDumbTransaction } from "@/features/invoices/order-confirmation-invoices/hooks/use-order-confirmation-invoice-dumb-transaction";
import useUpdateOrderConfirmationInvoice, {
  UpdateOrderConfirmationInvoiceRequest,
} from "@/features/invoices/order-confirmation-invoices/hooks/use-update-order-confirmation-invoice";
import { isStoredObject, Price } from "@/features/types";
import PriceUtils from "@/features/ui/utils/price-utils";
import OrderConfirmationInvoiceFormModal from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-form-modal";
import { OrderConfirmationInvoiceFormFieldsModal } from "@/features/invoices/order-confirmation-invoices/upload-order-confirmation-invoice-page-modal";
import { LightPayment } from "@/features/invoices/payment/payment.type";
import {
  CROSS_TRADER_REQUIRED_MESSAGE,
  isCrossTraderMissing,
} from "@/features/invoices/proforma-invoices/cross-trade-fee";

interface Props {
  orderConfirmationInvoiceId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// 오더 컨펌 인보이스 수정 모달 — 프로포마 수정 모달처럼 목록에서 눌러 바로 고친다.
// 값 채우기·저장 요청은 OC 상세 페이지(order-confirmation-invoice-detail-page)와 같다.
export default function EditOrderConfirmationInvoiceModal({
  orderConfirmationInvoiceId,
  isOpen,
  onClose,
  onSuccess,
}: Props) {
  const identity = useIdentity();
  const { tTitle } = useI18nHelper();
  const { error: showError, warning: showWarning } = useAppToasts();

  const key = useMemo(():
    | {
        parameter: GetDenseOrderConfirmationInvoiceParameter;
        request: GetDenseOrderConfirmationInvoiceRequest;
      }
    | undefined => {
    if (identity?.company && orderConfirmationInvoiceId) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
          orderConfirmationInvoiceId,
        },
        // 모달은 OC 응답만으로 충분하다 — 송금 요청·연결 오더를 dense 로 다시 불러오면
        // 오더·라인시트 조회가 줄줄이 붙는다(송금 정보와 오더 id 는 OC 응답에 이미 있다)
        request: { skipDensePayments: true, skipOrderSheetSets: true },
      };
    }
    return undefined;
  }, [identity, orderConfirmationInvoiceId]);

  const { data: orderConfirmationInvoice, mutate: mutateOrderConfirmationInvoice } =
    useGetDenseOrderConfirmationInvoice(
    key?.parameter,
    key?.request,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  const initialValues = useMemo((): OrderConfirmationInvoiceFormFieldsModal => {
    if (orderConfirmationInvoice) {
      // 디파짓·총액은 저장된 인보이스 금액 그대로 보여 준다(3자무역 수수료를 곱하지 않는다, 2026-10-30)
      const isCrossTrade =
        orderConfirmationInvoice.issuedByCompany?.type === "CROSS_TRADER";
      return {
        id: orderConfirmationInvoice.id,
        isCrossTrade,
        originallyIssuedBy: isCrossTrade
          ? orderConfirmationInvoice.originallyIssuedByCompany ?? undefined
          : undefined,
        number: orderConfirmationInvoice.number || undefined,
        status: orderConfirmationInvoice.status,
        season: orderConfirmationInvoice.season,
        isRemittedToBoutique: orderConfirmationInvoice.isRemittedToBoutique,
        isRemittedToAgency: orderConfirmationInvoice.isRemittedToAgency,
        orderedBy: orderConfirmationInvoice.issuedToCompany,
        issuedBy: orderConfirmationInvoice.issuedByCompany,
        issuedThrough: orderConfirmationInvoice.issuedThroughCompany,
        issuedOn: orderConfirmationInvoice.issuedOn,
        paymentMethod: orderConfirmationInvoice.paymentMethod,
        paidByAgency: orderConfirmationInvoice.paidBy === "AGENCY",
        exposedToBuyer: orderConfirmationInvoice.isPublic,
        privateComment: orderConfirmationInvoice.privateComment,
        publicComment: orderConfirmationInvoice.publicComment,
        orderSheetSets: orderConfirmationInvoice.orderSheetSets,
        file: orderConfirmationInvoice.file || undefined,
        name: orderConfirmationInvoice.name,
        orderConfirmationInvoiceDetailList:
          orderConfirmationInvoice.orderConfirmationInvoiceDetailList.map(
            (ocDetail) => {
              const orderSheetSet = ocDetail.orderSheetSet
                ? orderConfirmationInvoice.orderSheetSets.find(
                    (oss) => oss.id === ocDetail.orderSheetSet?.id
                  )
                : undefined;

              return {
                ...ocDetail,
                orderSheetSet,
              };
            }
          ),
        proformaInvoices: orderConfirmationInvoice.proformaInvoices.map(
          (pInvoice) => ({ id: pInvoice.id })
        ),
        expectedRemainingDepositAmount:
          orderConfirmationInvoice.expectedRemainingDepositAmount,
        creditInvoices: orderConfirmationInvoice.creditInvoices.map(
          (cInvoice) => ({ id: cInvoice.id })
        ),
        depositInvoice: orderConfirmationInvoice.depositInvoice || undefined,
        totalAmount: orderConfirmationInvoice.totalAmount,
        totalRequiredAmount: orderConfirmationInvoice.totalRequiredAmount,
        payments: orderConfirmationInvoice.payments,
        exchangeRate: orderConfirmationInvoice.exchangeRate ?? undefined,
        bankFee: orderConfirmationInvoice.bankFee ?? undefined,
      };
    }

    return {
      exposedToBuyer: identity?.company?.type !== "BOUTIQUE",
      paidByAgency: false,
      orderSheetSets: [],
      orderConfirmationInvoiceDetailList: [],
      proformaInvoices: [],
      creditInvoices: [],
      payments: [],
    };
  }, [orderConfirmationInvoice, identity]);

  const validateSchema = useOrderConfirmationInvoiceFormValidationSchema();
  const validate = useCallback(
    async (values: OrderConfirmationInvoiceFormFieldsModal) => {
      try {
        await validateSchema.validate(values, { abortEarly: false });
      } catch (e) {
        showError(e);
        return e;
      }
      if (isCrossTraderMissing(values)) {
        showWarning(CROSS_TRADER_REQUIRED_MESSAGE);
        return { issuedBy: CROSS_TRADER_REQUIRED_MESSAGE };
      }
    },
    [showError, showWarning, validateSchema]
  );

  const { addUpdate } = useOrderConfirmationInvoiceDumbTransaction();
  const { fire: fireUpdateOrderConfirmationInvoice } =
    useUpdateOrderConfirmationInvoice();

  const toRequest = useCallback(
    (values: OrderConfirmationInvoiceFormFieldsModal) => {
      return {
        id: values.id!,
        number: values.number!,
        season: values.season!,
        status: values.status!,
        name: values.name!,
        issuedToCompany: values.orderedBy!,
        issuedByCompany: values.issuedBy!,
        originallyIssuedByCompany: values.isCrossTrade
          ? values.originallyIssuedBy ?? null
          : null,
        // 3자무역 수수료율은 더 이상 쓰지 않는다 — 0 을 보내 서버 값도 지운다
        crossTradeFeeRate: 0,
        issuedThroughCompany: values.issuedThrough!,
        issuedOn: values.issuedOn!,
        paidBy: values.paidByAgency ? ("AGENCY" as const) : ("BUYER" as const),
        paymentMethod: values.paymentMethod!,
        // 브랜드별 금액은 보내지 않는다 — 서버가 기존 브랜드 행을 잇고 총액·총 디파짓을 비율로 다시 나눈다(2026-10-30)
        orderConfirmationInvoiceDetailList: [],
        orderSheetSets: values.orderSheetSets.map((oss) => ({ id: oss.id })),
        isPublic: values.exposedToBuyer,
        publicComment: values.publicComment || null,
        privateComment: values.privateComment || null,
        file: values.file!,
        totalAmount: PriceUtils.placeholder(values.totalAmount, "EUR") as Price,
        totalRequiredAmount: PriceUtils.placeholder(values.totalRequiredAmount, "EUR") as Price,
        exchangeRate: values.exchangeRate || null,
        bankFee: PriceUtils.placeholder(values.bankFee, "KRW"),
      };
    },
    []
  );

  const handleSubmit = useCallback(
    async (values: OrderConfirmationInvoiceFormFieldsModal) => {
      if (!identity?.company || !values.id) return false;

      const parameter = {
        by: identity.company.type,
        companyId: identity.company.id,
        orderConfirmationInvoiceId: values.id,
      };
      const request = toRequest(values);

      try {
        // 파일을 새로 올리지 않았으면 바로 저장하고, 새 파일이면 업로드 후 저장하는 큐로 보낸다.
        // 요청 모양은 상세 페이지와 같다(updatedAt·실제 환율 등은 서버가 채워 보내지 않는다).
        if (isStoredObject(request.file)) {
          await fireUpdateOrderConfirmationInvoice(
            parameter,
            request as unknown as UpdateOrderConfirmationInvoiceRequest
          );
        } else {
          await addUpdate(
            parameter,
            request as unknown as Parameters<typeof addUpdate>[1]
          );
        }
        onSuccess?.();
        onClose();
      } catch (e) {
        showError(e);
        return false;
      }

      return new Promise<boolean>((resolve) => {
        setTimeout(() => resolve(true), 500);
      });
    },
    [identity, toRequest, fireUpdateOrderConfirmationInvoice, addUpdate, onSuccess, onClose, showError]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius={"8px"} overflow={"hidden"} maxWidth={"1600px"}>
        <ModalBody display="flex" flexDirection={"column"} gap={"12px"}>
          <Formik<OrderConfirmationInvoiceFormFieldsModal>
            enableReinitialize={true}
            initialValues={initialValues}
            validate={validate}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            onSubmit={(values) => handleSubmit(values)}
          >
            {(props) => (
              <Grid
                as={"form"}
                height={"100%"}
                width={"100%"}
                gridTemplateColumns={"1fr"}
                gridTemplateRows={"auto auto 1fr"}
              >
                <AppListHeader
                  headers={[
                    <Heading key={"EditOrderConfirmationInvoice"} as={"h2"} fontSize={"14px"}>
                      {tTitle("order_confirmation_invoice")}
                    </Heading>,
                  ]}
                  filters={[]}
                  actions={[
                    <AppButton
                      key="Cancel"
                      isDisabled={props.isSubmitting}
                      onClick={onClose}
                    >
                      {tTitle("cancel")}
                    </AppButton>,
                    <AppButton
                      key="Save"
                      isDisabled={props.isSubmitting}
                      isLoading={props.isSubmitting}
                      onClick={() => props.submitForm()}
                    >
                      {tTitle("save")}
                    </AppButton>,
                  ]}
                />
                <OrderConfirmationInvoiceFormModal
                  formik={props}
                  isEditing={true}
                  payments={orderConfirmationInvoice?.payments as LightPayment[] | undefined}
                  // 영수증을 올려 송금 완료 처리하면 송금 상태와 목록을 다시 불러온다
                  onReceiptModalClose={() => {
                    mutateOrderConfirmationInvoice();
                    onSuccess?.();
                  }}
                />
              </Grid>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
